import { Alert, AlertDescription, AlertIcon, Skeleton } from '@chakra-ui/react';
import React, {
  useContext,
  useMemo,
  useState,
  createContext,
  useCallback,
} from 'react';

import { error as commonError } from '@finley-technologies/common';

import {
  useGetAthenaSchemaForOrganizationQuery,
  AthenaTableGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

export type TableSchemas = {
  tableSchemas: AthenaTableGQLType[];
  setTableSchemas: React.Dispatch<React.SetStateAction<AthenaTableGQLType[]>>;
};

export type AthenaSchemaContextType = {
  athenaMetadata: TableSchemas | null;
  isLoading: boolean;
  AthenaSchemaContextLoadingOrErrorComponent: (props: {
    renderLoading?: React.ReactElement;
    children: React.ReactNode;
  }) => React.ReactElement | null;
  getTableSchema: (tableName: string) => AthenaTableGQLType | undefined;
};

export const AthenaSchemaContext = createContext<AthenaSchemaContextType>({
  athenaMetadata: null,
  isLoading: true,
  AthenaSchemaContextLoadingOrErrorComponent: () => null,
  getTableSchema: () => undefined,
});

/**
 * # ⚠️ WARNING ⚠️ __does not__ block children from rendering while loading. Use `AthenaSchemaContextLoadingOrErrorComponent` to wrap children to show a loading state
 *
 * A hook to use the Athena schema context
 * - `athenaMetadata` The Athena table schemas for the organization
 *   - `tableSchemas` The Athena table schemas
 *   - `setTableSchemas` A function to set the Athena table schemas
 * - `loading` - Whether the Athena table schemas are still loading
 * - `AthenaSchemaContextLoadingOrErrorComponent` - A wrapper component that can be used to show a loading state while the Athena table schemas are still loading ⚠️ should be used to wrap the children of the component that uses the Athena schema context
 */
export const useAthenaSchemaContext = () => {
  const context = useContext(AthenaSchemaContext);
  if (context === null) {
    throw new commonError.FinleyError('AthenaSchemaContext must be present');
  }

  return context;
};

type AthenaSchemaContextLoadingOrErrorComponentProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  renderLoading?: React.ReactElement;
  // eslint-disable-next-line react/no-unused-prop-types
  renderError?: React.ReactElement;
  children: React.ReactNode;
};

type AthenaSchemaProviderProps = {
  children: React.ReactNode;
  organizationId: string;
};
export const AthenaSchemaProvider = ({
  organizationId,
  children,
}: AthenaSchemaProviderProps) => {
  const [tableSchemas, setTableSchemas] = useState<AthenaTableGQLType[]>([]);

  const {
    loading: isLoading,
    error: isError,
  } = useGetAthenaSchemaForOrganizationQuery({
    variables: { organizationId },
    onCompleted: (response) => {
      setTableSchemas(response.athenaTables);
    },
    onError: () => {
      setTableSchemas([]);
    },
  });

  // !TODO @eldarshamukhamedov this should be a useMemo, not useCallback. Consumers should receive a
  // pre-rendered component's JSX markup, not the component itself. Alternatively, rendering of
  // loading/error states could be delegated to the consumers.
  const AthenaSchemaContextLoadingOrErrorComponent = useCallback(
    ({
      children: wrapperChildren,
      renderLoading,
      renderError,
    }: AthenaSchemaContextLoadingOrErrorComponentProps) => {
      const loadingComponent = renderLoading ?? <Skeleton w="100%" h="100%" />;

      if (isError) {
        return (
          <>
            {renderError || (
              <Alert status="error">
                <AlertIcon />
                <AlertDescription>
                  Encountered an error when loading your portfolio. Please
                  contact Finley support for more information.
                </AlertDescription>
              </Alert>
            )}
          </>
        );
      }

      return <>{isLoading ? loadingComponent : wrapperChildren}</>;
    },
    [isLoading, isError],
  );

  const getTableSchema = useCallback(
    (tableName: string) => {
      const tableSchema = tableSchemas.find(
        ({ tableName: currTableName }) => currTableName === tableName,
      );
      return tableSchema;
    },
    [tableSchemas],
  );

  const context = useMemo(() => {
    return {
      athenaMetadata: { tableSchemas, setTableSchemas },
      isLoading,
      AthenaSchemaContextLoadingOrErrorComponent,
      getTableSchema,
    };
  }, [
    getTableSchema,
    tableSchemas,
    isLoading,
    AthenaSchemaContextLoadingOrErrorComponent,
  ]);

  return (
    <AthenaSchemaContext.Provider value={context}>
      {children}
    </AthenaSchemaContext.Provider>
  );
};
